/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { FormattedMessage, FormattedNumber } from 'js/lib/coursera.react-intl';

import { Typography, Typography2, typography2Color } from '@coursera/cds-core';
import { StarFilledIcon, StarHalfFilledIcon, StarIcon } from '@coursera/cds-icons';
import { Link } from '@coursera/event-pulse/react';

import { ENROLLMENT_COUNT_THRESHOLD } from 'bundles/unified-description-page-common/constants';
import { useEventingProductData } from 'bundles/unified-description-page-common/contexts/EventingContext';
import { useXDPRedesignPhase1Data } from 'bundles/unified-description-page-common/providers/specialization/XDPRedesignPhase1Provider';
import { SPECIALIZATION_NAV_ITEMS } from 'bundles/unified-description-page-common/utils/getPageNavigationLinks';

import _t from 'i18n!nls/xdp';

type Props = {
  productRating?: number | null;
  totalReviewCount?: number | null;
  enrollmentCount?: number | null;
  isSpecialization?: boolean;
};

const originalStyles = {
  wrapper: css`
    display: flex;
    align-items: center;
    align-content: center;
  `,
  iconContainer: css`
    padding: 0 var(--cds-spacing-100) 0 var(--cds-spacing-50);
    display: flex;
    align-items: center;
  `,
};

const redesignStyles = {
  ...originalStyles,
  iconContainer: css`
    padding: 0;
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-50);
  `,
  link: css`
    ${typography2Color.body};
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-50);

    &:hover,
    &:visited,
    &:focus-visible {
      color: inherit !important;
    }
  `,
};

const KeyInformationHorizontalReviewsItem = ({
  productRating,
  totalReviewCount,
  enrollmentCount,
  isSpecialization = false,
}: Props) => {
  const { enableXDPRedesignPhase1, enableXDPRedesignKIM, enableXDPRedesignHero } = useXDPRedesignPhase1Data();
  const enableXDPRedesign = enableXDPRedesignPhase1 || (enableXDPRedesignKIM && enableXDPRedesignHero);
  const styles = enableXDPRedesign ? redesignStyles : originalStyles;
  const eventingProductData = useEventingProductData();

  if (!productRating || !totalReviewCount) return null;

  const shouldDisplayEnrollmentCount = enrollmentCount && enrollmentCount > ENROLLMENT_COUNT_THRESHOLD;
  const fixedRating = productRating.toFixed(1);
  const formattedReviewCount = totalReviewCount.toLocaleString();
  const reviewsText = isSpecialization
    ? _t('from #{totalReviewCount} reviews of courses in this program', {
        totalReviewCount: formattedReviewCount,
      })
    : _t('#{totalReviewCount} reviews', {
        totalReviewCount: formattedReviewCount,
      });

  const renderStars = () => {
    const stars = [];
    const roundedRating = Math.round(productRating * 2) / 2; // Round to nearest 0.5

    for (let i = 1; i <= 5; i += 1) {
      if (i <= roundedRating) {
        stars.push(<StarFilledIcon key={i} color="default" size="medium" />);
      } else if (i - 0.5 === roundedRating) {
        stars.push(<StarHalfFilledIcon key={i} color="default" size="medium" />);
      } else {
        stars.push(<StarIcon key={i} color="default" size="medium" />);
      }
    }

    return stars;
  };

  if (enableXDPRedesign) {
    return (
      <React.Fragment>
        <div css={styles.wrapper}>
          <Typography2
            aria-label={_t('#{fixedRating} stars', { fixedRating })}
            variant="subtitleMedium"
            css={redesignStyles.link}
            component={Link}
            href={`#${SPECIALIZATION_NAV_ITEMS.COURSES}`}
            trackingData={{
              button: { name: 'description_page_view_ratings' },
              pageSection: {
                sectionName: 'kim',
              },
              ...eventingProductData,
            }}
          >
            <div css={styles.iconContainer}>{renderStars()}</div>
            {fixedRating}
          </Typography2>
        </div>
        <Typography2 component="p" variant="bodySecondary" color="supportText">
          {reviewsText}
        </Typography2>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <div css={styles.wrapper}>
        <Typography
          aria-label={_t('#{fixedRating} stars', { fixedRating })}
          role="img"
          aria-roledescription="rating"
          variant="h2semibold"
          component="div"
        >
          <span aria-hidden="true">{fixedRating}</span>
        </Typography>
        <div css={styles.iconContainer}>
          <StarFilledIcon color="interactive" size="medium" />
        </div>
      </div>
      <Typography2 component="p" variant="bodySecondary" color="supportText">
        {reviewsText}
      </Typography2>

      {shouldDisplayEnrollmentCount && (
        <Typography2 component="p" variant="bodySecondary" color="supportText">
          <FormattedMessage message={_t('{count} enrolled')} count={<FormattedNumber value={enrollmentCount} />} />
        </Typography2>
      )}
    </React.Fragment>
  );
};

export default KeyInformationHorizontalReviewsItem;
